import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Label, Spinner, TextInput, Toast } from '../../lib';
import apiServices from '../../services/apiServices';
import { GetPortalDocumentsAdmin, PortalDocument } from '../../services/dtos';

// D1 staff surface: upload a PDF and watch the render queue chew through it.
// Everything per-document (publish, review mode, audience, due date,
// re-render, archive) lives on the D5 configure page — this list is upload +
// status + jump-off. Server re-checks the DocumentAdmin role on every call.

const POLL_MS = 2500;

// Whole-document QA preview (every page, scrolling, watermarked) — cookie
// JWT authenticates the plain link; the server burns the viewer's watermark
// into every image it serves.
function previewUrl(id: number): string {
  return `${apiServices.apiUrl()}/admin/documents/${id}/view`;
}

async function uploadDocument(title: string, docType: string, file: File): Promise<void> {
  const form = new FormData();
  form.append('Title', title);
  if (docType) form.append('DocType', docType);
  form.append('file', file, file.name);
  const res = await fetch(`${apiServices.apiUrl()}/admin/documents`, {
    method: 'POST',
    credentials: 'include',
    body: form,
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    let msg = `Upload failed (${res.status})`;
    try {
      const body = await res.json();
      msg = body?.responseStatus?.message || msg;
    } catch {
      /* keep the status message */
    }
    throw new Error(msg);
  }
}

function fmtSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

function StateBadge({ doc }: { doc: PortalDocument }) {
  if (doc.renderState === 'Ready')
    return (
      <span className="rounded px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
        Ready
      </span>
    );
  if (doc.renderState === 'Failed')
    return (
      <span
        title={doc.renderError || undefined}
        className="rounded px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
      >
        Failed
      </span>
    );
  const progress = doc.pageCount
    ? ` ${doc.renderedPages}/${doc.pageCount}`
    : '';
  return (
    <span className="rounded px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
      {doc.renderState === 'Rendering' ? `Rendering${progress}` : 'Queued'}
    </span>
  );
}

const DocumentAdminPage: React.FC = () => {
  const [docs, setDocs] = useState<PortalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const refresh = (archived = includeArchived) =>
    apiServices.client
      .get(new GetPortalDocumentsAdmin({ includeArchived: archived }))
      .then((data: PortalDocument[]) => {
        setDocs(data);
        setError(null);
      })
      .catch(() => setError('Failed to load documents'))
      .then(() => setLoading(false));

  useEffect(() => {
    refresh(includeArchived);
  }, [includeArchived]);

  // poll while the queue is working so the status column moves on its own
  const anyActive = docs.some(
    (d) => d.renderState === 'Queued' || d.renderState === 'Rendering'
  );
  useEffect(() => {
    if (!anyActive) return;
    const t = setInterval(() => refresh(includeArchived), POLL_MS);
    return () => clearInterval(t);
  }, [anyActive, includeArchived]);

  const submitUpload = () => {
    if (!title.trim() || !file || uploading) return;
    setUploading(true);
    setUploadError(null);
    uploadDocument(title.trim(), docType.trim(), file)
      .then(() => {
        setTitle('');
        setDocType('');
        setFile(null);
        if (fileInput.current) fileInput.current.value = '';
        return refresh();
      })
      .catch((e: Error) => setUploadError(e.message))
      .then(() => setUploading(false));
  };

  return (
    <div className="mx-auto w-full flex max-w-5xl flex-col gap-6 dark:text-white">
      <div>
        <h2 className="text-2xl font-bold">Document Admin</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Upload PDFs for the member document area. Every page is rendered to
          images in the background; members will only ever see watermarked
          copies.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Upload a document
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="mb-1">
              <Label htmlFor="doc-title" value="Title" />
            </div>
            <TextInput
              id="doc-title"
              type="text"
              placeholder="2026 Convention Center Agreement"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div>
            <div className="mb-1">
              <Label htmlFor="doc-type" value="Type (optional)" />
            </div>
            <TextInput
              id="doc-type"
              type="text"
              placeholder="Contract, Policy, …"
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
            />
          </div>
        </div>
        <div>
          <div className="mb-1">
            <Label htmlFor="doc-file" value="PDF (up to 50 MB)" />
          </div>
          <input
            id="doc-file"
            ref={fileInput}
            type="file"
            accept=".pdf,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-gray-600 dark:text-gray-300 file:mr-3 file:rounded file:border-0 file:bg-indigo-600 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-indigo-700"
          />
        </div>
        {uploadError && <Toast color="failure">{uploadError}</Toast>}
        <div>
          <Button
            size="sm"
            disabled={!title.trim() || !file || uploading}
            onClick={submitUpload}
          >
            {uploading ? 'Uploading…' : 'Upload & Render'}
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600"
          />
          Show archived
        </label>
        {anyActive && (
          <span className="text-xs text-gray-400 dark:text-gray-500">
            Rendering — updates automatically
          </span>
        )}
      </div>

      {error && <Toast color="failure">{error}</Toast>}
      {loading && <Spinner color="info" />}

      {!loading && docs.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center text-sm text-gray-500 dark:text-gray-400">
          No documents yet — upload the first one above.
        </div>
      )}

      {docs.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-3 py-2">Title</th>
                <th className="px-3 py-2 w-28">Type</th>
                <th className="px-3 py-2 w-20">Pages</th>
                <th className="px-3 py-2 w-24">PDF size</th>
                <th className="px-3 py-2 w-32">Status</th>
                <th className="px-3 py-2 w-44">Uploaded</th>
                <th className="px-3 py-2 w-56">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
              {docs.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900 dark:text-white break-words">
                      {d.title}
                      {d.isPublished && (
                        <span className="ml-2 rounded px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          Published
                        </span>
                      )}
                      {d.isArchived && (
                        <span className="ml-2 text-xs font-normal text-gray-400">
                          (archived)
                        </span>
                      )}
                    </div>
                    {d.renderState === 'Failed' && d.renderError && (
                      <div className="mt-0.5 text-xs text-red-600 dark:text-red-400 break-words">
                        {d.renderError}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                    {d.docType || '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                    {d.pageCount ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                    {fmtSize(d.pdfBytes)}
                  </td>
                  <td className="px-3 py-2">
                    <StateBadge doc={d} />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                    {d.uploadedByName}
                    <br />
                    {fmtDate(d.createdAtUtc)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium">
                      <Link
                        to={`/doc-admin/doc/${d.id}`}
                        className="text-indigo-600 hover:underline dark:text-indigo-400"
                      >
                        Configure
                      </Link>
                      {(d.reviewMode === 'Acknowledge' ||
                        d.reviewMode === 'TypedName') && (
                        <Link
                          to={`/doc-admin/roster/${d.id}`}
                          className="text-indigo-600 hover:underline dark:text-indigo-400"
                        >
                          Roster
                        </Link>
                      )}
                      {d.renderState === 'Ready' && (
                        <a
                          href={previewUrl(d.id)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-indigo-600 hover:underline dark:text-indigo-400"
                        >
                          Preview
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DocumentAdminPage;
